import type { JdContent } from './schemas/jd-content.schema';
import { JD_RATE_FACT_INDEX, JD_TEAM_PHRASES } from './schemas/jd-content.schema';
import type { ProfileContent } from './schemas/profile-content.schema';
import {
  PROFILE_FACTS_MAX,
  PROFILE_FACTS_MIN,
  profileLanguagesFactIndex,
} from './schemas/profile-content.schema';

/**
 * Content rules (spec §6.4) as validators. Errors block saving unless force=true (with a reason);
 * warnings are shown but do not block.
 */

export type CheckLevel = 'error' | 'warning';

export interface CheckResult {
  code: string;
  level: CheckLevel;
  message: string;
  path?: string;
}

export interface CheckSummary {
  ok: boolean;
  errors: CheckResult[];
  warnings: CheckResult[];
  results: CheckResult[];
}

export function summarizeChecks(results: CheckResult[]): CheckSummary {
  const errors = results.filter((r) => r.level === 'error');
  const warnings = results.filter((r) => r.level === 'warning');
  return { ok: errors.length === 0, errors, warnings, results };
}

/** Walks any JSON value and yields [path, string]. */
export function collectStrings(value: unknown, path = ''): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const walk = (v: unknown, p: string) => {
    if (typeof v === 'string') out.push([p, v]);
    else if (Array.isArray(v)) v.forEach((x, i) => walk(x, `${p}[${i}]`));
    else if (v && typeof v === 'object') {
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) walk(x, p ? `${p}.${k}` : k);
    }
  };
  walk(value, path);
  return out;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Case-insensitive, boundary-aware search for each term. Returns the terms found. */
export function findForbiddenTerms(text: string, terms: readonly string[]): string[] {
  const found: string[] = [];
  for (const term of terms) {
    const t = term.trim();
    if (!t) continue;
    const re = new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(t)}(?=$|[^\\p{L}\\p{N}])`, 'iu');
    if (re.test(text)) found.push(t);
  }
  return found;
}

export function checkForbiddenTerms(
  content: unknown,
  terms: readonly string[],
  basePath = '',
): CheckResult[] {
  const results: CheckResult[] = [];
  const cleanTerms = terms.map((t) => t.trim()).filter((t) => t.length >= 2);
  if (cleanTerms.length === 0) return results;
  for (const [path, text] of collectStrings(content, basePath)) {
    const hits = findForbiddenTerms(text, cleanTerms);
    for (const hit of hits) {
      results.push({
        code: 'FORBIDDEN_TERM',
        level: 'error',
        message: `Forbidden term "${hit}" found`,
        path,
      });
    }
  }
  return results;
}

/** File and folder names must not contain forbidden terms either (spec §6.4 rule 1). */
export function checkFileName(name: string, terms: readonly string[]): CheckResult[] {
  return findForbiddenTerms(name, terms).map((hit) => ({
    code: 'FORBIDDEN_TERM_IN_NAME',
    level: 'error' as const,
    message: `Forbidden term "${hit}" in file or folder name "${name}"`,
  }));
}

const MARGIN_WORDS_RE = /\b(margin|markup|mark-up|marge|aufschlag|client rate|kundensatz|reduced by)\b/i;
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE_RE =
  /(?:\+|00)\d{1,3}[\s\-.]?\(?\d{1,4}\)?(?:[\s\-.]?\d{2,4}){2,4}|\b0\d{2,4}[\s\-./]?\d{3,4}[\s\-./]?\d{3,5}\b/;
const LINKEDIN_RE = /linkedin\.com|xing\.com|github\.com\/[A-Za-z0-9-]+/i;
const DOB_RE = /\b(date of birth|born on|geburtsdatum|geboren am|geb\.)\b/i;
const RATE_RE =
  /(€\s*\d|\d\s*€|\bEUR\b|\beuro\b|\bper hour\b|\/\s*h\b|\bStundensatz\b|\bTagessatz\b|\bper day\b|\brate\b)/i;
const ADDRESS_RE = /\b(str\.|straße|strasse|street|st\.|bd\.|bulevardul|strada)\s+[A-Za-zÄÖÜäöüß.-]+\s+\d+/i;

/** Page limits of the 2026 templates (A4, Chromium). */
export const JD_MAX_PAGES = 3;
export const PROFILE_MAX_PAGES = 4;
/** With an admin override (allowExtraPage). */
export const PROFILE_MAX_PAGES_OVERRIDE = 5;

export interface JdCheckOptions {
  forbiddenTerms: readonly string[];
  /** The rate text computed by §5 for this position; the Rate fact must equal it. */
  expectedRateText?: string;
  pageCount?: number | null;
  maxPages?: number;
}

export function checkJdContent(content: JdContent, opts: JdCheckOptions): CheckResult[] {
  const results: CheckResult[] = [];
  const lang = content.lang === 'de' ? 'de' : 'en';
  const maxPages = opts.maxPages ?? JD_MAX_PAGES;

  results.push(...checkForbiddenTerms(content, opts.forbiddenTerms));

  if (content.facts.length !== 8) {
    results.push({
      code: 'FACTS_COUNT',
      level: 'error',
      message: 'A JD needs exactly 8 facts',
      path: 'facts',
    });
  }
  content.facts.forEach((f, i) => {
    if (f.value.length > 110) {
      results.push({
        code: 'FACT_TOO_LONG',
        level: 'warning',
        message: `Fact "${f.label}" is probably longer than 2 lines (${f.value.length} chars)`,
        path: `facts[${i}].value`,
      });
    }
  });

  const rateFact = content.facts[JD_RATE_FACT_INDEX];
  if (opts.expectedRateText && rateFact && rateFact.value.trim() !== opts.expectedRateText.trim()) {
    results.push({
      code: 'RATE_FACT_MISMATCH',
      level: 'error',
      message: `The Rate fact must be "${opts.expectedRateText}" (computed from the client rate and the effective margin), got "${rateFact.value}"`,
      path: `facts[${JD_RATE_FACT_INDEX}].value`,
    });
  }

  for (const [path, text] of collectStrings(content)) {
    if (MARGIN_WORDS_RE.test(text)) {
      results.push({
        code: 'MARGIN_MENTIONED',
        level: 'error',
        message: 'A JD must not mention the margin, markup or client rate',
        path,
      });
    }
  }

  const teamText = (Array.isArray(content.team) ? content.team.join(' ') : content.team).toLowerCase();
  const phrases = JD_TEAM_PHRASES[lang];
  if (!phrases.some((p) => teamText.includes(p))) {
    results.push({
      code: 'TEAM_PHRASE_MISSING',
      level: 'error',
      message:
        lang === 'de'
          ? 'Der Abschnitt "Team" muss "unter Koordination von data-claw.de" enthalten'
          : `The team section must contain "as part of the client's team under the coordination of data-claw.de"`,
      path: 'team',
    });
  }

  const rangeWarn = (
    arr: readonly string[] | undefined,
    min: number,
    max: number,
    path: string,
    label: string,
  ) => {
    if (!arr) return;
    if (arr.length < min || arr.length > max) {
      results.push({
        code: 'LIST_LENGTH',
        level: 'warning',
        message: `${label}: ${min}–${max} bullets recommended, got ${arr.length}`,
        path,
      });
    }
  };
  rangeWarn(content.responsibilities, 4, 7, 'responsibilities', 'Responsibilities');
  rangeWarn(content.mustHave, 4, 7, 'mustHave', 'Must-have');
  if (content.niceToHave && content.niceToHave.length > 5) {
    rangeWarn(content.niceToHave, 0, 5, 'niceToHave', 'Nice to have');
  }
  rangeWarn(content.process, 3, 5, 'process', 'Process');
  if (
    Array.isArray(content.stack) &&
    content.stack.length > 0 &&
    (content.stack.length < 3 || content.stack.length > 5)
  ) {
    results.push({
      code: 'LIST_LENGTH',
      level: 'warning',
      message: `Tech stack: 3–5 rows recommended, got ${content.stack.length}`,
      path: 'stack',
    });
  }

  if (opts.pageCount != null) {
    if (opts.pageCount > maxPages) {
      results.push({
        code: 'PAGE_LIMIT',
        level: 'error',
        message: `A JD must be 1–${maxPages} pages, got ${opts.pageCount}`,
      });
    }
  }
  return results;
}

export interface ProfileCheckOptions {
  forbiddenTerms: readonly string[];
  /** Expected display name ("First S." or "Candidate DC-C-…") for this candidate. */
  expectedDisplayName?: string;
  pageCount?: number | null;
  /** PROFILE_MAX_PAGES by default; PROFILE_MAX_PAGES_OVERRIDE only with an admin override. */
  maxPages?: number;
  /** Languages known from the candidate record; each must appear in the profile. */
  candidateLanguages?: ReadonlyArray<{ language: string; level?: string | null }>;
}

const DISPLAY_NAME_RE =
  /^(\p{Lu}[\p{L}'’-]+(?: \p{Lu}[\p{L}'’-]+)* \p{Lu}\.|Candidate DC-C-\d{4}-\d{4,}|Kandidat(?:in)? DC-C-\d{4}-\d{4,})$/u;

export function checkProfileContent(content: ProfileContent, opts: ProfileCheckOptions): CheckResult[] {
  const results: CheckResult[] = [];
  const maxPages = opts.maxPages ?? PROFILE_MAX_PAGES;

  results.push(...checkForbiddenTerms(content, opts.forbiddenTerms));

  const dn = content.candidate.displayName.trim();
  if (!DISPLAY_NAME_RE.test(dn)) {
    results.push({
      code: 'DISPLAY_NAME_FORMAT',
      level: 'error',
      message: `Display name must be "First S." or "Candidate DC-C-YYYY-NNNN", got "${dn}"`,
      path: 'candidate.displayName',
    });
  }
  if (opts.expectedDisplayName && dn !== opts.expectedDisplayName.trim()) {
    results.push({
      code: 'DISPLAY_NAME_MISMATCH',
      level: 'error',
      message: `Display name must be "${opts.expectedDisplayName}" for this candidate`,
      path: 'candidate.displayName',
    });
  }

  for (const [path, text] of collectStrings(content)) {
    if (path === 'preparedFor' || path.startsWith('contact')) continue;
    if (EMAIL_RE.test(text)) {
      results.push({
        code: 'PII_EMAIL',
        level: 'error',
        message: 'An e-mail address must not appear in a profile',
        path,
      });
    }
    if (PHONE_RE.test(text)) {
      results.push({
        code: 'PII_PHONE',
        level: 'error',
        message: 'A phone number must not appear in a profile',
        path,
      });
    }
    if (LINKEDIN_RE.test(text)) {
      results.push({
        code: 'PII_LINK',
        level: 'error',
        message: 'Social/profile links must not appear in a profile',
        path,
      });
    }
    if (DOB_RE.test(text)) {
      results.push({
        code: 'PII_DOB',
        level: 'error',
        message: 'Date of birth must not appear in a profile',
        path,
      });
    }
    if (ADDRESS_RE.test(text)) {
      results.push({
        code: 'PII_ADDRESS',
        level: 'error',
        message: 'A street address must not appear in a profile',
        path,
      });
    }
    if (RATE_RE.test(text)) {
      results.push({
        code: 'RATE_IN_PROFILE',
        level: 'error',
        message: 'Rates never appear in a profile',
        path,
      });
    }
  }

  const factCount = content.candidate.facts.length;
  if (factCount < PROFILE_FACTS_MIN || factCount > PROFILE_FACTS_MAX) {
    results.push({
      code: 'FACTS_COUNT',
      level: 'error',
      message: `A profile needs ${PROFILE_FACTS_MIN} to ${PROFILE_FACTS_MAX} facts`,
      path: 'candidate.facts',
    });
  }
  content.candidate.facts.forEach((f, i) => {
    if (f.value.length > 110) {
      results.push({
        code: 'FACT_TOO_LONG',
        level: 'warning',
        message: `Fact "${f.label}" is probably longer than 2 lines`,
        path: `candidate.facts[${i}].value`,
      });
    }
  });

  const languagesIndex = profileLanguagesFactIndex(content.candidate.facts);
  const langFact = content.candidate.facts[languagesIndex];
  const langText = `${langFact?.value ?? ''} ${content.languages ?? ''}`.toLowerCase();
  if (opts.candidateLanguages && opts.candidateLanguages.length > 0) {
    for (const l of opts.candidateLanguages) {
      const names = languageAliases(l.language);
      if (!names.some((n) => langText.includes(n))) {
        results.push({
          code: 'LANGUAGE_MISSING',
          level: 'error',
          message: `Language "${l.language}" from the CV is missing in the profile`,
          path: `candidate.facts[${languagesIndex}].value`,
        });
      }
    }
  }
  if (
    langFact &&
    !/\b([ABC][12]|native|muttersprache|fluent|beginner|not stated|nicht angegeben|grundkenntnisse|fliessend|fließend)\b/i.test(
      langFact.value,
    )
  ) {
    results.push({
      code: 'LANGUAGE_LEVELS',
      level: 'warning',
      message: 'Every language should carry a level (CEFR, native, beginner or "not stated")',
      path: `candidate.facts[${languagesIndex}].value`,
    });
  }

  const bullets = content.assessment?.bullets ?? [];
  if (content.assessment && (bullets.length < 3 || bullets.length > 5)) {
    results.push({
      code: 'LIST_LENGTH',
      level: 'warning',
      message: `Assessment: 3–5 bullets recommended, got ${bullets.length}`,
      path: 'assessment.bullets',
    });
  }

  if (opts.pageCount != null && opts.pageCount > maxPages) {
    results.push({
      code: 'PAGE_LIMIT',
      level: 'error',
      message: `A profile must be ${maxPages} pages at most, got ${opts.pageCount}`,
    });
  }
  return results;
}

const LANGUAGE_NAMES: Record<string, string[]> = {
  english: ['english', 'englisch', 'engleza'],
  german: ['german', 'deutsch', 'germana'],
  romanian: ['romanian', 'rumänisch', 'rumanisch', 'romana', 'română'],
  french: ['french', 'französisch', 'franzosisch', 'franceza'],
  spanish: ['spanish', 'spanisch'],
  italian: ['italian', 'italienisch'],
  hungarian: ['hungarian', 'ungarisch', 'maghiara'],
  polish: ['polish', 'polnisch'],
  dutch: ['dutch', 'niederländisch', 'niederlandisch'],
  portuguese: ['portuguese', 'portugiesisch'],
  russian: ['russian', 'russisch'],
  ukrainian: ['ukrainian', 'ukrainisch'],
  turkish: ['turkish', 'türkisch'],
};

export function languageAliases(language: string): string[] {
  const key = language.trim().toLowerCase();
  for (const [canonical, aliases] of Object.entries(LANGUAGE_NAMES)) {
    if (canonical === key || aliases.includes(key)) return [canonical, ...aliases];
  }
  return [key];
}
